import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { candidateApi } from '../services/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '../components/ui/Textarea'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type { CandidateState, Question } from '../types'

function CandidatePage() {
  const { formUuid } = useParams<{ formUuid: string }>()
  const [state, setState] = useState<CandidateState>({ current_index: 0, answers: [], interview_complete: false })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [answer, setAnswer] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Load state on mount
  useEffect(() => {
    if (!formUuid) {
      setError('UUID לא נמצא בכתובת')
      setLoading(false)
      return
    }

    const loadState = async () => {
      try {
        const response = await candidateApi.getState(formUuid)
        setState(response.state || { current_index: 0, answers: [], interview_complete: false })
      } catch (err: any) {
        setError(err.response?.data?.message || 'שגיאה בטעינת מצב הריאיון')
        console.error('Failed to load state:', err)
      } finally {
        setLoading(false)
      }
    }
    loadState()
  }, [formUuid])

  // Hidden tab-leave tracking. Nothing about this is shown to the candidate.
  useEffect(() => {
    if (!formUuid || loading || state.interview_complete) return

    const report = (event: 'hidden' | 'visible') => {
      candidateApi.recordActivity(formUuid, event).catch(() => {})
    }

    const onVisibility = () => {
      report(document.hidden ? 'hidden' : 'visible')
    }

    report(document.hidden ? 'hidden' : 'visible')

    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [formUuid, loading, state.interview_complete, state.current_index])

  // Clear the draft when the question changes
  useEffect(() => {
    if (state.current_index !== undefined) {
      setAnswer('')
    }
  }, [state.current_index])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!answer.trim() || !formUuid) return

    setSubmitting(true)
    setError(null)

    try {
      const response = await candidateApi.submitAnswer(formUuid, answer)
      setState(response.state || state)
      setAnswer('')
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה בשליחת תשובה')
    } finally {
      setSubmitting(false)
    }
  }

  if (!formUuid) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <Card className="border border-red-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-red-700 mb-2">שגיאה</h2>
              <p className="text-sm text-gray-600">UUID לא נמצא בכתובת</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (state.interview_complete) {
    return (
      <div className="bg-gray-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="mb-6 border border-green-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-center">
                <h1 className="text-2xl font-semibold mb-4 text-gray-900">תודה על השתתפותך בראיון!</h1>
                <p className="text-gray-700 leading-relaxed mb-4">
                  הריאיון הושלם בהצלחה. אנו מעריכים את הזמן והמאמץ שהשקעת.
                </p>
                <p className="text-gray-600 text-sm">
                  נבחן את תשובותיך ונחזור אליך בהקדם האפשרי.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const currentQuestion: Question | undefined = state.pending_question

  return (
    <div className="bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-center text-gray-900 mb-2">
            שאלת ריאיון {state.current_index + 1}
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {currentQuestion && (
          <>
            <Card className="mb-6 border border-gray-200 shadow-sm">
              <CardContent className="pt-6">
                <h3 className="text-base font-medium text-gray-900">{currentQuestion.text}</h3>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {currentQuestion.q_type === 'MCQ' && currentQuestion.options ? (
                    <div className="space-y-2">
                      {currentQuestion.options.map((option, idx) => (
                        <label
                          key={idx}
                          className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="radio"
                            name="answer"
                            value={option}
                            checked={answer === option}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="ml-3 h-4 w-4 text-gray-900 focus:ring-gray-900"
                            required
                          />
                          <span className="flex-1 text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <Textarea
                      label="תשובתך"
                      name="answer"
                      rows={6}
                      placeholder="תשובתך כאן..."
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      required
                    />
                  )}

                  <Button type="submit" disabled={submitting || !answer.trim()} className="w-full">
                    {submitting ? 'שולח...' : 'שלח תשובה'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}

export default CandidatePage
